// Споделената таблица на модул „Справки" — TanStack Table v8 (headless) с наш
// Tailwind маркъп и antd само за попъпите.
//
// Дава: сортиране (вкл. по няколко колони с Shift), филтър в самата колона,
// скриване/показване и пренареждане на колони, преоразмеряване, плътност,
// запомняне на подредбата, ред „Общо", пагинация, мобилни карти.
//
// Два режима. `client` — всичко се смята върху подадените редове. `server` —
// сортирането, филтрирането и страницирането се вдигат до BE през
// `ServerTableBridge`, а редът „Общо" идва от него (страницата не може да
// сумира период, който не е зареден).

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
} from "@tanstack/react-table";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Input, Spin } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../hooks/useViewport";
import { DataCell } from "./DataCell";
import { HeaderCell } from "./HeaderCell";
import { ColumnsPanel, type ColumnsPanelItem } from "./ColumnsPanel";
import { Pager } from "./Pager";
import { MobileCards } from "./MobileCards";
import { MobileControls } from "./MobileControls";
import { TotalsRow } from "./TotalsRow";
import { useDataTableState } from "./useDataTableState";
import {
  checklistFilterFn,
  dateRangeFilterFn,
  displayTextSortingFn,
  numberRangeFilterFn,
  textFilterFn,
} from "./filterFns";
import type { CellOptions, DataTableProps } from "./types";

const DENSITY_ROW: Record<string, string> = {
  compact: "h-[38px] px-3",
  normal: "h-[45px] px-3",
  roomy: "h-[54px] px-4",
};

const DENSITY_HEADER: Record<string, string> = {
  compact: "px-3 py-1.5",
  normal: "px-3 py-2",
  roomy: "px-4 py-2.5",
};

/**
 * Кой вид филтър с коя функция се прилага. БЕЗ това мапване TanStack пада на
 * `filterFn: "auto"` и избира по типа на СТОЙНОСТТА: клетка-обект (`chip`,
 * `text`, `dot`) минава през `equals` → нито един ред не съвпада и списъкът
 * се изпразва. Регистърът в `filterFns` по-долу без това е мъртъв код.
 */
const FILTER_FN_BY_KIND: Record<string, string> = {
  text: "textContains",
  checklist: "checklist",
  numberRange: "numberRange",
  dateRange: "dateRange",
};

/** Видовете клетки, чиято стойност е ОБЕКТ — сортират се по показания текст. */
const OBJECT_CELL_KINDS = new Set(["text", "dot", "chip", "bar"]);

/** Дясно подравнените видове клетки — числата се четат по последната цифра. */
const RIGHT_ALIGNED = new Set(["num", "money", "pct", "bar"]);

const alignOf = (options: CellOptions | undefined, override?: string) => {
  if (override === "left" || override === "right" || override === "center") {
    return override;
  }
  return options && RIGHT_ALIGNED.has(options.kind) ? "right" : "left";
};

export const DataTable = <TData,>({
  tableId,
  data,
  columns,
  getRowId,
  mode = "client",
  server,
  initialSorting = [],
  loading = false,
  refreshing = false,
  title,
  toolbarRight,
  toolbarFilters,
  search,
  onRowClick,
  rowTone,
  emptyTitle,
  emptyHint,
  mobileCard,
  pageSize: initialPageSize = 20,
  pageSizeOptions = [10, 20, 50, 100, 200],
  onFilteredRowsChange,
  ariaLabel,
}: DataTableProps<TData>) => {
  const { t } = useTranslation();
  const { isNarrow, isTablet } = useViewport();
  const isServer = mode === "server";

  const columnIds = useMemo(
    () => columns.map((column) => String(column.id ?? column.accessorKey)),
    [columns],
  );

  // Колоната описва филтъра си декларативно (`meta.filter`); тук описанието
  // се превежда до истинска `filterFn`, а клетките-обекти получават компаратор
  // по показания текст. Справката не пише нито едното.
  const tableColumns = useMemo(
    () =>
      columns.map((column) => {
        const meta = column.meta;
        const next = { ...column };
        if (meta?.filter && !column.filterFn) {
          next.filterFn = FILTER_FN_BY_KIND[meta.filter.kind];
        }
        if (
          meta?.cell &&
          OBJECT_CELL_KINDS.has(meta.cell.kind) &&
          !column.sortingFn
        ) {
          next.sortingFn = "displayText";
        }
        return next;
      }),
    [columns],
  );

  const state = useDataTableState({
    tableId,
    columnIds,
    initialSorting,
    initialPageSize,
  });

  const [clientPageIndex, setClientPageIndex] = useState(0);
  const pageIndex = isServer ? (server?.pageIndex ?? 0) : clientPageIndex;

  const setPageIndex = useCallback(
    (next: number) => {
      if (isServer) server?.onPageIndexChange(next);
      else setClientPageIndex(next);
    },
    [isServer, server],
  );

  // `hideBelow` е ПОДРАЗБИРАНЕ за тесен екран, не ключалка: важи само докато
  // потребителят не се е произнесъл за тази колона. Иначе на таблет (< 1024px,
  // тоест повечето POS терминали) „Бон №" и „Плащане" изчезват, отметката в
  // панела „Колони" стои вдигната и включването ѝ не прави нищо — панелът лъже.
  // Изборът не се пише в запомненото състояние от само себе си, така че
  // завъртане на телефона не гаси колони и на компютъра.
  const effectiveVisibility = useMemo(() => {
    const merged: Record<string, boolean> = { ...state.columnVisibility };
    for (const column of columns) {
      const hideBelow = column.meta?.hideBelow;
      const defaultHidden = column.meta?.defaultHidden;
      if (!hideBelow && !defaultHidden) continue;
      const columnId = String(column.id ?? column.accessorKey);
      if (columnId in state.columnVisibility) continue;
      const hidden =
        defaultHidden ||
        (hideBelow === "narrow" ? isNarrow : hideBelow ? isNarrow || isTablet : false);
      if (hidden) merged[columnId] = false;
    }
    return merged;
  }, [state.columnVisibility, columns, isNarrow, isTablet]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(state.sorting) : updater;
    state.setSorting(next);
    setPageIndex(0);
    if (isServer) server?.onSortingChange(next);
  };

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (
    updater,
  ) => {
    const next =
      typeof updater === "function" ? updater(state.columnFilters) : updater;
    state.setColumnFilters(next);
    setPageIndex(0);
    if (isServer) server?.onColumnFiltersChange(next);
  };

  const table = useReactTable<TData>({
    data: data as TData[],
    columns: tableColumns,
    getRowId,
    state: {
      sorting: state.sorting,
      columnFilters: state.columnFilters,
      columnOrder: state.columnOrder,
      columnVisibility: effectiveVisibility,
      columnSizing: state.columnSizing,
      pagination: { pageIndex, pageSize: state.pageSize },
    },
    filterFns: {
      checklist: checklistFilterFn,
      textContains: textFilterFn,
      numberRange: numberRangeFilterFn,
      dateRange: dateRangeFilterFn,
    },
    sortingFns: { displayText: displayTextSortingFn },
    // Клик по заглавие ЗАМЕНЯ подредбата. Мулти-сортът е достъпен с Shift, но
    // сървърният режим праща само първия ключ — включен по подразбиране би
    // светил стрелки на две колони, без списъкът да се променя.
    enableMultiSort: false,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnOrderChange: state.setColumnOrder,
    onColumnVisibilityChange: state.setColumnVisibility,
    onColumnSizingChange: state.setColumnSizing,
    columnResizeMode: "onChange",
    defaultColumn: { minSize: 56, size: 130 },
    manualSorting: isServer,
    manualFiltering: isServer,
    manualPagination: isServer,
    rowCount: isServer ? server?.rowCount : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isServer ? undefined : getSortedRowModel(),
    getFilteredRowModel: isServer ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isServer ? undefined : getPaginationRowModel(),
    getFacetedRowModel: isServer ? undefined : getFacetedRowModel(),
    getFacetedUniqueValues: isServer ? undefined : getFacetedUniqueValues(),
    getFacetedMinMaxValues: isServer ? undefined : getFacetedMinMaxValues(),
  });

  const rows = table.getRowModel().rows;

  // Клиентският режим филтрира и подрежда ТУК, а справката пази суровия набор
  // — без този мост Excel изнася нещо друго, не таблицата на екрана. Редовете
  // са без страниране: изнася се целият филтриран резултат, не текущата
  // страница. В сървърен режим няма какво да се вдига — филтрите вече са в
  // заявката, с която страницата тегли данните.
  const sortedRows = isServer ? null : table.getSortedRowModel().rows;
  useEffect(() => {
    if (!onFilteredRowsChange || !sortedRows) return;
    onFilteredRowsChange(sortedRows.map((row) => row.original));
  }, [sortedRows, onFilteredRowsChange]);

  const rowCount = isServer
    ? (server?.rowCount ?? 0)
    : table.getFilteredRowModel().rows.length;

  // Свие ли се наборът (по-къс период, нов филтър), текущата страница може да
  // остане извън обхвата — тогава списъкът е празен, а пейджърът е скрит и
  // няма връщане назад. Затова индексът се притегля към последната страница.
  const lastPageIndex = Math.max(
    0,
    Math.ceil(rowCount / Math.max(1, state.pageSize)) - 1,
  );
  useEffect(() => {
    if (rowCount > 0 && pageIndex > lastPageIndex) setPageIndex(lastPageIndex);
  }, [rowCount, pageIndex, lastPageIndex, setPageIndex]);

  // БЕЗ `KeyboardSensor`: неговите listener-и стоят върху същия бутон, който
  // сортира, и хващат Space/Enter с `preventDefault()` — от клавиатурата
  // заглавието започваше влачене вместо да сортира. Клавиатурният път за
  // пренареждане са стрелките в панела „Колони".
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Забавянето е задължително на тъч: без него влаченето на колона се бие
    // със скрола на списъка върху POS терминала.
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    state.setColumnOrder((previous) => {
      const order = previous.length ? previous : columnIds;
      return arrayMove(
        order,
        order.indexOf(String(active.id)),
        order.indexOf(String(over.id)),
      );
    });
  };

  const panelItems: ColumnsPanelItem[] = table
    .getAllLeafColumns()
    .map((column) => ({
      id: column.id,
      label:
        column.columnDef.meta?.label ||
        (typeof column.columnDef.header === "string"
          ? column.columnDef.header
          : "") ||
        column.id,
      visible: column.getIsVisible(),
      canHide: column.getCanHide() && !column.columnDef.meta?.alwaysVisible,
    }));

  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map((column) => column.id);

  const isEmpty = !loading && rows.length === 0;

  // Празното състояние е РЕД в таблицата, а не заместител на цялата таблица:
  // скрие ли се `<thead>`, изчезва и менюто на колоната, тоест филтърът, който
  // е изпразнил списъка, става неотменим без презареждане.
  const emptyBlock = (
    <div className="px-5 py-12 text-center">
      <p className="text-[14px] font-bold text-soft">
        {emptyTitle ?? t("dataTable.emptyTitle")}
      </p>
      <p className="mx-auto mt-1 max-w-[44em] text-[12.5px] font-medium leading-[1.5] text-muted">
        {emptyHint ?? t("dataTable.emptyHint")}
      </p>
    </div>
  );

  return (
    <section className="flex flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface">
      {/* Без разделител с `flex-1`: в `flex-wrap` контейнер той се разтяга и
          изтласква контролите на нов ред. `justify-between` държи заглавието
          вляво и групата вдясно на един ред. */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-shade-2 px-4 py-3">
        {title}

        <div className="flex flex-wrap items-center gap-2">
          {search ? (
            <Input
              value={search.value}
              onChange={(event) => search.onChange(event.target.value)}
              placeholder={search.placeholder}
              allowClear
              prefix={<SearchOutlined className="text-muted" />}
              // `!` — antd cssinjs е извън Tailwind layer-ите и без него
              // affix обвивката налага собствената си ширина.
              className={`!w-[220px] !rounded-lg ${
                isNarrow ? "!text-[16px]" : "!text-[13px]"
              }`}
            />
          ) : null}

          {toolbarFilters}

          {/* Един изход от всички филтри наведнъж. Поединично се махат от
              менюто на своята колона, но при три активни филтъра върху три
              различни колони това са три попъпа — и „Върни подредбата" не ги
              докосва. */}
          {state.columnFilters.length ? (
            <button
              type="button"
              onClick={() => table.resetColumnFilters()}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-brand-tint2 bg-brand-tint px-3 text-[12.5px] font-bold text-brand-deep-ink transition-colors hover:bg-brand-tint2"
            >
              <FilterOutlined className="text-[12px]" />
              {t("dataTable.clearAllFilters")}
              <span className="font-pos-mono text-[11px]">
                {state.columnFilters.length}
              </span>
            </button>
          ) : null}

          {isNarrow && mobileCard ? (
            // В режим „карти" няма хедър, значи няма и меню на колоната:
            // панелът „Колони" би бил безсмислен (картата не гледа видимост),
            // а подредбата и филтрите без това са недостижими на телефон.
            <MobileControls
              columns={table.getAllLeafColumns()}
              isServer={isServer}
              onClearFilters={() => table.resetColumnFilters()}
            />
          ) : (
          <ColumnsPanel
            items={panelItems}
            onToggle={(columnId, visible) =>
              state.setColumnVisibility((previous) => ({
                ...previous,
                [columnId]: visible,
              }))
            }
            density={state.density}
            onDensityChange={state.setDensity}
            onReset={state.resetLayout}
            hasCustomLayout={state.hasCustomLayout}
          />
          )}

          {toolbarRight}
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Spin />
        </div>
      ) : isNarrow && mobileCard ? (
        isEmpty ? (
          emptyBlock
        ) : (
          <MobileCards
            rows={rows}
            renderCard={mobileCard}
            onRowClick={onRowClick}
            refreshing={refreshing}
          />
        )
      ) : (
        <div className={`overflow-x-auto ${refreshing ? "opacity-60" : ""}`}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <table
              aria-label={ariaLabel}
              className="w-full border-collapse text-left"
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={visibleColumnIds}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header) => (
                        <HeaderCell
                          key={header.id}
                          header={header}
                          align={alignOf(
                            header.column.columnDef.meta?.cell,
                            header.column.columnDef.meta?.align,
                          )}
                          facets={
                            isServer
                              ? null
                              : (header.column.getFacetedUniqueValues() ?? null)
                          }
                          bounds={
                            isServer
                              ? null
                              : (header.column.getFacetedMinMaxValues() as
                                  | [number, number]
                                  | null)
                          }
                          onFilterChange={(columnId, value) =>
                            table.getColumn(columnId)?.setFilterValue(value)
                          }
                          headerPadding={DENSITY_HEADER[state.density]}
                        />
                      ))}
                    </SortableContext>
                  </tr>
                ))}
              </thead>

              <tbody>
                {isEmpty ? (
                  <tr>
                    <td colSpan={Math.max(1, visibleColumnIds.length)}>
                      {emptyBlock}
                    </td>
                  </tr>
                ) : null}
                {rows.map((row) => {
                  const tone = rowTone?.(row.original) ?? null;
                  return (
                    <tr
                      key={row.id}
                      onClick={() => onRowClick?.(row.original)}
                      // Кликаемият ред се отваря и от клавиатурата: Tab до него,
                      // Enter/интервал. Само когато фокусът е на самия ред —
                      // бутон или поле в клетката си обработва клавиша сам.
                      tabIndex={onRowClick ? 0 : undefined}
                      onKeyDown={
                        onRowClick
                          ? (event) => {
                              if (event.target !== event.currentTarget) return;
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              onRowClick(row.original);
                            }
                          : undefined
                      }
                      className={`border-b border-shade-1 transition-colors ${
                        tone === "brand"
                          ? "bg-brand-tint/40"
                          : tone === "danger"
                            ? "bg-danger-tint/40"
                            : ""
                      } ${
                        onRowClick
                          ? "cursor-pointer outline-none hover:bg-shade-1 focus-visible:bg-shade-2"
                          : ""
                      }`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta;
                        const align = alignOf(meta?.cell, meta?.align);
                        return (
                          <td
                            key={cell.id}
                            className={`${DENSITY_ROW[state.density]} align-middle whitespace-nowrap leading-[normal] ${
                              align === "right"
                                ? "text-right"
                                : align === "center"
                                  ? "text-center"
                                  : "text-left"
                            }`}
                          >
                            {meta?.cell ? (
                              <span
                                className={`flex ${
                                  align === "right"
                                    ? "justify-end"
                                    : align === "center"
                                      ? "justify-center"
                                      : "justify-start"
                                }`}
                              >
                                <DataCell
                                  options={meta.cell}
                                  value={cell.getValue()}
                                />
                              </span>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <TotalsRow
                table={table}
                density={state.density}
                serverTotals={isServer ? server?.totals : undefined}
                rowCount={rowCount}
              />
            </table>
          </DndContext>
        </div>
      )}

      {!loading && rowCount > 0 ? (
        <Pager
          pageIndex={pageIndex}
          pageSize={state.pageSize}
          pageSizeOptions={pageSizeOptions}
          rowCount={rowCount}
          onPageIndexChange={setPageIndex}
          onPageSizeChange={(next) => {
            state.setPageSize(next);
            setPageIndex(0);
            server?.onPageSizeChange?.(next);
          }}
        />
      ) : null}
    </section>
  );
};
